import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  MdArrowBack,
  MdArrowForward,
  MdCheckCircle,
  MdSchedule,
  MdPerson,
  MdAttachMoney,
  MdError,
  MdCalendarToday,
  MdAccessTime,
  MdPhone,
  MdEmail,
  MdPersonOutline,
  MdClose,
} from "react-icons/md";
import { getServices } from "../../services/inventory.service";
import { getEmployee } from "../../services/employee.service";
import { getSlots, createBooking } from "../../services/booking.service";
import { PaymentMethodDialog } from "./PaymentMethodDialog";
import { AccountLoginDialog } from "./accountLogin";
import { createCustomer } from "../../services/customer.service";
import { toast } from "react-toastify";
import { AWS_URL } from "../../services/http.service";
import { getStorefrontPath } from "./storefrontPaths";

const ServiceBookingPage = () => {
  const { bussinessUuid, businessName, serviceId } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [service, setService] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [slots, setSlots] = useState([]);
  const [availableEmployeesForSlot, setAvailableEmployeesForSlot] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [loading, setLoading] = useState(true);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [finalAmount, setFinalAmount] = useState(0);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedSlot, setSelectedSlot] = useState("");
  const [slotAllowsWithoutEmployee, setSlotAllowsWithoutEmployee] = useState(false);
  const [skippedEmployeeStep, setSkippedEmployeeStep] = useState(false);
  const [bookingInProgress, setBookingInProgress] = useState(false);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [signupPromptRequired, setSignupPromptRequired] = useState(false);
  const [formValues, setFormValues] = useState({
    name: "",
    email: "",
    mobile: "",
  });

  const getProfileDefaults = (profile) => ({
    name: profile?.name || "",
    email: profile?.email || "",
    mobile: profile?.customer_mobile || profile?.phone || profile?.mobile || "",
  });

  const applyProfileDefaultsToForm = (profile) => {
    const defaults = getProfileDefaults(profile);
    setFormValues((prev) => ({
      ...prev,
      ...defaults,
    }));
  };

  useEffect(() => {
    if (!bussinessUuid) return;

    const savedUser = localStorage.getItem(`user_${bussinessUuid}`);

    if (savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        const isGuest = parsedUser?.isGuest === true;

        if (!isGuest) {
          setUserProfile(parsedUser);
          applyProfileDefaultsToForm(parsedUser);
          setAccountDialogOpen(false);
          return;
        }
      } catch (error) {
        console.error("Failed to parse saved user profile:", error);
      }
    }

    setUserProfile(null);
  }, [bussinessUuid]);

  // Load service and employees
  useEffect(() => {
    const loadData = async () => {
      try {
        const servicesRes = await getServices(bussinessUuid);
        const servicesList = servicesRes?.data?.data || servicesRes?.data || [];
        const selectedService = servicesList.find(
          (s) => s.serviceId === serviceId || s._id === serviceId
        );
        setService(selectedService);

        const employeesRes = await getEmployee(bussinessUuid, { limit: 100 });
        setEmployees(employeesRes?.data?.data || []);
      } catch (error) {
        console.error("Error loading data:", error);
        toast.error("Failed to load service details");
      } finally {
        setLoading(false);
      }
    };

    if (bussinessUuid && serviceId) {
      loadData();
    }
  }, [bussinessUuid, serviceId]);

  // Load slots when date changes
  useEffect(() => {
    if (!selectedDate || !bussinessUuid || !serviceId) return;

    const loadSlots = async () => {
      setLoadingSlots(true);
      try {
        const slotsRes = await getSlots(
          bussinessUuid,
          serviceId,
          selectedDate,
          undefined
        );
        setSlots(slotsRes ?? []);
        setSelectedSlot("");
        setAvailableEmployeesForSlot([]);
        setSlotAllowsWithoutEmployee(false);
      } catch (error) {
        console.error("Error loading slots:", error);
        toast.error("Failed to load available slots");
      } finally {
        setLoadingSlots(false);
      }
    };

    loadSlots();
  }, [selectedDate, bussinessUuid, serviceId]);

  // Update available employees when slot is selected
  useEffect(() => {
    if (!selectedSlot || slots.length === 0) {
      setAvailableEmployeesForSlot([]);
      return;
    }

    const selectedSlotData = slots.find((s) => s.time === selectedSlot);
    if (selectedSlotData) {
      setAvailableEmployeesForSlot(selectedSlotData.availableEmployees || []);
      setSlotAllowsWithoutEmployee(Boolean(selectedSlotData.allowWithoutEmployee));
      setSelectedEmployee("");
    }
  }, [selectedSlot, slots]);

  const validationSchemas = [
    Yup.object().shape({
      date: Yup.string().required("Please select a date"),
    }),
    Yup.object().shape({
      time: Yup.string().required("Please select a time slot"),
    }),
    Yup.object().shape({
      employee: Yup.string().required("Please select an employee"),
    }),
    Yup.object().shape({
      name: Yup.string().required("Name is required").min(2, "Name must be at least 2 characters"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      mobile: Yup.string()
        .required("Phone number is required")
        .matches(/^[0-9]{10}$/, "Phone number must be 10 digits"),
    }),
  ];

  const initialValues = {
    date: selectedDate,
    time: selectedSlot,
    employee: selectedEmployee,
    name: formValues.name,
    email: formValues.email,
    mobile: formValues.mobile,
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedSlot("");
  };

  const isAuthenticatedCustomer = !!userProfile && userProfile.isGuest !== true;

  const requireCustomerSignup = () => {
    if (isAuthenticatedCustomer) return true;
    setSignupPromptRequired(true);
    setAccountDialogOpen(true);
    toast.info("Please sign up or login before selecting a booking date");
    return false;
  };

  const handleSignupDialogClose = () => {
    setAccountDialogOpen(false);

    const savedUserRaw = localStorage.getItem(`user_${bussinessUuid}`);
    let hasAuthenticatedUser = false;

    if (savedUserRaw) {
      try {
        const parsedUser = JSON.parse(savedUserRaw);
        hasAuthenticatedUser = parsedUser?.isGuest !== true;
      } catch (error) {
        hasAuthenticatedUser = false;
      }
    }

    if (!isAuthenticatedCustomer && !hasAuthenticatedUser && signupPromptRequired) {
      navigate(-1);
    }
  };

  const handleEmployeeSelect = (empId) => {
    setSelectedEmployee(empId);
  };

  const navigateToServices = () => {
    navigate(
      getStorefrontPath({
        businessName,
        bussinessUuid,
      }),
      { state: { activeTab: "services" } }
    );
  };

  const handleSlotSelect = (time) => {
    setSelectedSlot(time);
  };

  const isTodayDate = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    return dateStr === todayStr;
  };

  const isPastSlotForToday = (slotTime) => {
    if (!isTodayDate(selectedDate) || !slotTime) return false;

    const [hourStr, minuteStr] = String(slotTime).split(":");
    const slotHour = Number(hourStr);
    const slotMinute = Number(minuteStr);

    if (Number.isNaN(slotHour) || Number.isNaN(slotMinute)) return false;

    const now = new Date();
    const slotDateTime = new Date(now);
    slotDateTime.setHours(slotHour, slotMinute, 0, 0);

    return slotDateTime.getTime() <= now.getTime();
  };

  const handleNextStep = (values) => {
    if (step < 4) {
      if (step === 1 && !isAuthenticatedCustomer) {
        requireCustomerSignup();
        return;
      }
      if (step === 1) setSelectedDate(values.date);
      if (step === 2) {
        setSelectedSlot(values.time);
        if (slotAllowsWithoutEmployee && availableEmployeesForSlot.length === 0) {
          setSkippedEmployeeStep(true);
          setSelectedEmployee("");
          setStep(4);
          return;
        }
        setSkippedEmployeeStep(false);
      }
      if (step === 3) setSelectedEmployee(values.employee);
      setStep(step + 1);
    }
  };

  const handlePreviousStep = () => {
    if (step === 4 && skippedEmployeeStep) {
      setStep(2);
      return;
    }
    if (step > 1) setStep(step - 1);
  };

  const handleFinalSubmit = (values) => {
    setFormValues({
      name: values.name,
      email: values.email,
      mobile: values.mobile,
    });
    
    const amount =
      service?.pricingType === "hourly"
        ? Number(service?.hourlyRate || 0) *
          (Number(service?.serviceDuration || 60) / 60)
        : Number(
            service?.discountAmount > 0
              ? service.discountAmount
              : service?.salePrice || 0
          );

    setFinalAmount(amount);
    setPaymentOpen(true);
  };

  const handleAccountLogin = async (user) => {
    try {
      const payload = {
        name: user.name,
        customer_mobile: user.phone || user.customer_mobile || user.mobile,
        email: user.email || "",
      };

      const response = await createCustomer(bussinessUuid, payload);

      let customerData;
      if (response?.data) {
        customerData = {
          ...response.data,
          customerId: response.data.customerId,
          _id: response.data._id || response.data.customerId,
          name: response.data.name || user.name,
          customer_mobile:
            response.data.customer_mobile || user.phone || user.customer_mobile || user.mobile,
          email: response.data.email || user.email || "",
          isGuest: false,
          isCustomer: true,
        };
      } else {
        customerData = {
          _id: `cust_${Date.now()}`,
          customerId: `cust_${Date.now()}`,
          name: user.name,
          customer_mobile: user.phone || user.customer_mobile || user.mobile || "",
          email: user.email || "",
          isGuest: false,
          isCustomer: true,
        };
      }

      setUserProfile(customerData);
      applyProfileDefaultsToForm(customerData);
      localStorage.setItem(`user_${bussinessUuid}`, JSON.stringify(customerData));
      localStorage.removeItem(`guest_${bussinessUuid}`);
      setSignupPromptRequired(false);
      setAccountDialogOpen(false);
      toast.success("Signup successful. You can continue booking.");
    } catch (error) {
      console.error("Error while saving customer profile:", error);

      const fallbackCustomer = {
        ...user,
        customer_mobile: user.phone || user.customer_mobile || user.mobile || "",
        isGuest: false,
        isCustomer: true,
      };

      setUserProfile(fallbackCustomer);
      applyProfileDefaultsToForm(fallbackCustomer);
      localStorage.setItem(`user_${bussinessUuid}`, JSON.stringify(fallbackCustomer));
      localStorage.removeItem(`guest_${bussinessUuid}`);
      setSignupPromptRequired(false);
      setAccountDialogOpen(false);
      toast.success("Signup successful. You can continue booking.");
    }
  };

  const handlePaymentConfirm = async (paymentMethod) => {
    setBookingInProgress(true);
    try {
      let employeeName = "";
      if (selectedEmployee && availableEmployeesForSlot.length > 0) {
        const selectedEmp = availableEmployeesForSlot.find(
          (e) => e.employeeId === selectedEmployee
        );
        employeeName = selectedEmp?.employeeName || "";
      }

      const bookingData = {
        serviceId: service.serviceId || service._id,
        customerId: userProfile?.customerId || userProfile?._id || "",
        date: selectedDate,
        time: selectedSlot,
        employeeId: selectedEmployee,
        employeeName: employeeName,
        name: formValues.name,
        email: formValues.email,
        mobile: formValues.mobile,
        amount: finalAmount,
        paymentMode: paymentMethod,
      };

      await createBooking(bussinessUuid, bookingData);
      toast.success("Service booked successfully!");
      setPaymentOpen(false);

      setTimeout(() => {
        navigateToServices();
      }, 2000);
    } catch (error) {
      console.error("Booking error:", error);
      toast.error(error.response?.data?.message || "Failed to book service");
    } finally {
      setBookingInProgress(false);
    }
  };

  let formikRef = React.useRef();

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center z-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading service details...</p>
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center z-50">
        <div className="text-center bg-white p-12 rounded-3xl shadow-2xl max-w-md">
          <MdError size={64} className="text-red-500 mx-auto mb-4" />
          <p className="text-red-600 text-lg font-medium mb-4">Service not found</p>
          <button
            onClick={() => navigate(-1)}
            className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full hover:shadow-lg transition-all duration-300"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center z-50 p-4 overflow-hidden">
      {/* Backdrop overlay */}
      <div className="absolute inset-0 bg-black/5 backdrop-blur-sm"></div>
      
      {/* Main Modal */}
      <div className="relative w-full max-w-5xl max-h-[95vh] bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col animate-fadeIn">
        {/* Close Button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors text-gray-600 hover:text-gray-800"
        >
          <MdClose size={24} />
        </button>

        {/* Header with Service Info */}
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-6 text-white flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm overflow-hidden flex-shrink-0 border-2 border-white/30">
              {service.serviceImage ? (
                <img
                  src={`${AWS_URL}${service.serviceImage}`}
                  alt={service.serviceName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <MdSchedule size={32} className="text-white/60" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold truncate">{service.serviceName}</h1>
              <p className="text-white/80 text-sm truncate">{service.serviceDescription}</p>
              <div className="flex flex-wrap gap-2 mt-1">
                <span className="flex items-center gap-1 text-xs bg-white/20 px-3 py-1 rounded-full backdrop-blur-sm">
                  <MdSchedule size={14} />
                  {service.serviceDuration} mins
                </span>
                <span className="flex items-center gap-1 text-xs bg-white/20 px-3 py-1 rounded-full backdrop-blur-sm">
                  <MdAttachMoney size={14} />
                  ₹
                  {service?.pricingType === "hourly"
                    ? (
                        Number(service?.hourlyRate || 0) *
                        (Number(service?.serviceDuration || 60) / 60)
                      ).toFixed(2)
                    : Number(service?.discountAmount > 0 ? service.discountAmount : service?.salePrice || 0).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Steps - Compact */}
        <div className="px-6 pt-4 pb-3 bg-gray-50/50 flex-shrink-0">
          <div className="flex justify-between items-center max-w-md mx-auto">
            {[1, 2, 3, 4].map((stepNum) => (
              <React.Fragment key={stepNum}>
                <div
                  className={`flex flex-col items-center transition-all ${
                    stepNum <= step ? "text-blue-600" : "text-gray-400"
                  }`}
                >
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-xs transition-all ${
                      stepNum <= step
                        ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg"
                        : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    {stepNum < step ? <MdCheckCircle size={16} /> : stepNum}
                  </div>
                  <span className="text-[10px] mt-1 font-medium">
                    {stepNum === 1 && "Date"}
                    {stepNum === 2 && "Time"}
                    {stepNum === 3 && "Expert"}
                    {stepNum === 4 && "Details"}
                  </span>
                </div>
                {stepNum < 4 && (
                  <div
                    className={`flex-1 h-0.5 mx-1 transition-all ${
                      stepNum < step ? "bg-gradient-to-r from-blue-600 to-indigo-600" : "bg-gray-300"
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            validationSchema={validationSchemas[step - 1]}
            onSubmit={(values) => {
              if (step < 4) {
                handleNextStep(values);
              } else {
                handleFinalSubmit(values);
              }
            }}
            enableReinitialize
          >
            {({ values, errors, touched, handleChange, handleBlur, isSubmitting, setFieldValue, setFieldTouched }) => (
              <Form className="h-full flex flex-col">
                {/* Content Area */}
                <div className="flex-1">
                  {/* Step 1: Date Selection */}
                  {step === 1 && (
                    <div className="space-y-4">
                      <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <MdCalendarToday className="text-blue-600" />
                        Select Your Date
                      </h2>
                      <div className="relative">
                        <input
                          type="date"
                          name="date"
                          value={values.date}
                          onFocus={() => {
                            requireCustomerSignup();
                          }}
                          onClick={() => {
                            requireCustomerSignup();
                          }}
                          onChange={(e) => {
                            if (!requireCustomerSignup()) return;
                            handleChange(e);
                            handleDateSelect(e.target.value);
                          }}
                          onBlur={handleBlur}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-gray-50/50"
                          min={new Date().toISOString().split('T')[0]}
                        />
                        {touched.date && errors.date && (
                          <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                            <MdError size={14} />
                            {errors.date}
                          </p>
                        )}
                      </div>
                      {selectedDate && (
                        <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                          <p className="text-sm text-gray-700">
                            <span className="font-semibold">Selected:</span>{" "}
                            {new Date(selectedDate).toLocaleDateString("en-IN", {
                              weekday: "long",
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 2: Time Slot Selection */}
                  {step === 2 && (
                    <div className="space-y-4">
                      <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <MdAccessTime className="text-blue-600" />
                        Available Time Slots
                      </h2>
                      <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold">Date:</span>{" "}
                          {new Date(selectedDate).toLocaleDateString("en-IN", {
                            weekday: "long",
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </p>
                      </div>

                      {loadingSlots ? (
                        <div className="flex justify-center py-8">
                          <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-200 border-t-blue-600"></div>
                        </div>
                      ) : slots.length > 0 ? (
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                          {slots.map((slot) => (
                            (() => {
                              const isPastTime = isPastSlotForToday(slot.time);
                              const hasAvailableEmployees =
                                slot.availableEmployees && slot.availableEmployees.length > 0;
                              const canBookWithoutEmployee = Boolean(slot.allowWithoutEmployee);
                              const isDisabled = Boolean(slot.booked) || isPastTime;

                              return (
                            <button
                              key={slot.time}
                              type="button"
                              onClick={() => {
                                if (isDisabled) return;
                                handleSlotSelect(slot.time);
                                setFieldValue("time", slot.time);
                                setFieldTouched("time", true, false);
                              }}
                              disabled={isDisabled}
                              className={`p-3 rounded-xl font-semibold transition-all border-2 text-sm ${
                                isDisabled
                                  ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                                  : selectedSlot === slot.time
                                  ? "border-blue-500 bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg"
                                  : "border-gray-200 hover:border-blue-400 hover:shadow-md bg-white"
                              }`}
                            >
                              <div>{slot.time}</div>
                              {isPastTime ? (
                                <div className="text-[10px] mt-0.5 opacity-75">Time passed</div>
                              ) : (
                                <div className="text-[10px] mt-0.5 opacity-75">
                                  {canBookWithoutEmployee
                                    ? "Book without employee selection"
                                    : hasAvailableEmployees
                                      ? `${slot.availableEmployees.length} available`
                                      : slot.booked
                                        ? "Fully booked"
                                        : "Available"}
                                </div>
                              )}
                            </button>
                              );
                            })()
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-10 bg-red-50 rounded-xl border-2 border-red-200">
                          <MdError size={40} className="text-red-500 mx-auto mb-2" />
                          <p className="text-base font-semibold text-red-700">No Slots Available</p>
                          <p className="text-sm text-red-600 mt-1">Please try a different date</p>
                        </div>
                      )}
                      {touched.time && errors.time && (
                        <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                          <MdError size={14} />
                          {errors.time}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Step 3: Employee Selection */}
                  {step === 3 && !slotAllowsWithoutEmployee && (
                    <div className="space-y-4">
                      <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <MdPerson className="text-blue-600" />
                        Choose Your Expert
                      </h2>
                      <div className="flex flex-wrap gap-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                        <span className="text-sm text-gray-700">
                          <span className="font-semibold">Date:</span>{" "}
                          {new Date(selectedDate).toLocaleDateString("en-IN", {
                            weekday: "short",
                            month: "short",
                            day: "numeric",
                          })}
                        </span>
                        <span className="text-sm text-gray-700">
                          <span className="font-semibold">Time:</span> {selectedSlot}
                        </span>
                      </div>

                      {availableEmployeesForSlot.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              handleEmployeeSelect("");
                              setFieldValue("employee", "");
                              setFieldTouched("employee", true, false);
                            }}
                            className={`p-3 rounded-xl border-2 transition-all text-left ${
                              selectedEmployee === ""
                                ? "border-blue-500 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-lg"
                                : "border-gray-200 hover:border-blue-400 bg-white"
                            }`}
                          >
                            <p className="font-semibold text-gray-900 text-sm">Any Available</p>
                            <p className="text-xs text-gray-500">Next available professional</p>
                          </button>

                          {availableEmployeesForSlot.map((emp) => (
                            <button
                              key={emp.employeeId}
                              type="button"
                              onClick={() => {
                                handleEmployeeSelect(emp.employeeId);
                                setFieldValue("employee", emp.employeeId);
                                setFieldTouched("employee", true, false);
                              }}
                              className={`p-3 rounded-xl border-2 transition-all text-left ${
                                selectedEmployee === emp.employeeId
                                  ? "border-blue-500 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-lg"
                                  : "border-gray-200 hover:border-blue-400 bg-white"
                              }`}
                            >
                              <p className="font-semibold text-gray-900 text-sm flex items-center gap-2">
                                <MdPersonOutline className="text-blue-600" />
                                {emp.employeeName}
                              </p>
                              <p className="text-xs text-green-600 flex items-center gap-1">
                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500"></span>
                                Available
                              </p>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-10 bg-red-50 rounded-xl border-2 border-red-200">
                          <MdError size={40} className="text-red-500 mx-auto mb-2" />
                          <p className="text-base font-semibold text-red-700">No Experts Available</p>
                          <p className="text-sm text-red-600 mt-1">Please select a different time</p>
                        </div>
                      )}
                      {touched.employee && errors.employee && (
                        <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                          <MdError size={14} />
                          {errors.employee}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Step 4: Personal Information */}
                  {step === 4 && (
                    <div className="space-y-4">
                      <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <MdPersonOutline className="text-blue-600" />
                        Your Information
                      </h2>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
                            <MdPersonOutline className="text-blue-600" />
                            Full Name *
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={values.name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-gray-50/50"
                            placeholder="Enter your full name"
                          />
                          {touched.name && errors.name && (
                            <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                              <MdError size={14} />
                              {errors.name}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
                            <MdEmail className="text-blue-600" />
                            Email Address *
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-gray-50/50"
                            placeholder="Enter your email"
                          />
                          {touched.email && errors.email && (
                            <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                              <MdError size={14} />
                              {errors.email}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
                            <MdPhone className="text-blue-600" />
                            Phone Number *
                          </label>
                          <input
                            type="tel"
                            name="mobile"
                            value={values.mobile}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-gray-50/50"
                            placeholder="10-digit phone number"
                          />
                          {touched.mobile && errors.mobile && (
                            <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                              <MdError size={14} />
                              {errors.mobile}
                            </p>
                          )}
                        </div>

                        {/* Booking Summary - Premium */}
                        <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 rounded-xl border-2 border-blue-200">
                          <h3 className="font-bold text-gray-900 mb-3 text-sm flex items-center gap-2">
                            <MdCheckCircle className="text-blue-600" />
                            Booking Summary
                          </h3>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between items-center border-b border-blue-200/50 pb-2">
                              <span className="text-gray-600 flex items-center gap-2">
                                <MdSchedule className="text-blue-600" size={14} />
                                Service
                              </span>
                              <span className="font-semibold text-gray-900 text-sm truncate max-w-[150px]">
                                {service.serviceName}
                              </span>
                            </div>
                            <div className="flex justify-between items-center border-b border-blue-200/50 pb-2">
                              <span className="text-gray-600 flex items-center gap-2">
                                <MdCalendarToday className="text-blue-600" size={14} />
                                Date
                              </span>
                              <span className="font-semibold text-gray-900 text-sm">
                                {new Date(selectedDate).toLocaleDateString("en-IN", {
                                  weekday: "short",
                                  month: "short",
                                  day: "numeric",
                                })}
                              </span>
                            </div>
                            <div className="flex justify-between items-center border-b border-blue-200/50 pb-2">
                              <span className="text-gray-600 flex items-center gap-2">
                                <MdAccessTime className="text-blue-600" size={14} />
                                Time
                              </span>
                              <span className="font-semibold text-gray-900 text-sm">
                                {selectedSlot}
                              </span>
                            </div>
                            {selectedEmployee && (
                              <div className="flex justify-between items-center border-b border-blue-200/50 pb-2">
                                <span className="text-gray-600 flex items-center gap-2">
                                  <MdPerson className="text-blue-600" size={14} />
                                  Expert
                                </span>
                                <span className="font-semibold text-gray-900 text-sm truncate max-w-[150px]">
                                  {availableEmployeesForSlot.find((e) => e.employeeId === selectedEmployee)
                                    ?.employeeName || "Selected"}
                                </span>
                              </div>
                            )}
                            <div className="flex justify-between items-center pt-2">
                              <span className="text-gray-900 font-bold">Total Amount</span>
                              <span className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                                ₹
                                {(
                                  service?.pricingType === "hourly"
                                    ? Number(service?.hourlyRate || 0) *
                                      (Number(service?.serviceDuration || 60) / 60)
                                    : Number(
                                        service?.discountAmount > 0
                                          ? service.discountAmount
                                          : service?.salePrice || 0
                                      )
                                ).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Navigation Buttons - Fixed at bottom */}
                <div className="flex justify-between mt-6 pt-4 border-t border-gray-200 gap-3 flex-shrink-0">
                  <button
                    type="button"
                    onClick={handlePreviousStep}
                    disabled={step === 1}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold transition-all text-sm ${
                      step === 1
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    <MdArrowBack size={18} />
                    Back
                  </button>

                  <button
                    type="submit"
                    disabled={bookingInProgress || (step === 1 && !selectedDate) || (step === 2 && slots.length === 0) || (step === 3 && (availableEmployeesForSlot.length === 0 || !selectedEmployee))}
                    className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-white transition-all shadow-lg text-sm ${
                      bookingInProgress || (step === 1 && !selectedDate) || (step === 2 && slots.length === 0) || (step === 3 && (availableEmployeesForSlot.length === 0 || !selectedEmployee))
                        ? "bg-gradient-to-r from-blue-400 to-indigo-400 cursor-not-allowed opacity-60"
                        : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:shadow-xl hover:scale-[1.02] transform transition-all"
                    }`}
                  >
                    {step === 4 ? "Confirm Booking" : "Next"}
                    <MdArrowForward size={18} />
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      {/* Payment Dialog */}
      <PaymentMethodDialog
        open={paymentOpen}
        total={finalAmount}
        onClose={() => setPaymentOpen(false)}
        onPay={handlePaymentConfirm}
      />

      <AccountLoginDialog
        open={accountDialogOpen}
        onClose={handleSignupDialogClose}
        onLogin={handleAccountLogin}
        onGuestMode={() => {}}
        showGuestButton={false}
      />

      {/* Add custom animation */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default ServiceBookingPage;